
import { useState, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';

export type SettingSection = 
  'account' | 
  'notifications' | 
  'system' | 
  'users' | 
  'datasources' |
  'devicegroups' |
  'protocols' |
  'security' |
  'ai' |
  'billing' |
  'sustainability' |
  'webhooks';

export const useSettingsState = () => {
  const [activeTab, setActiveTab] = useState('devices');
  const [editing, setEditing] = useState({
    account: false,
    notifications: false,
    system: false,
    users: false,
    datasources: false,
    devicegroups: false,
    protocols: false,
    security: false,
    ai: false,
    billing: false,
    sustainability: false,
    webhooks: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleEditToggle = useCallback((section: SettingSection) => {
    setEditing(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  }, []);

  const handleSave = useCallback(async (section: SettingSection) => {
    try {
      setIsSubmitting(true);
      
      // Simulating an API call with a timeout
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setEditing(prev => ({
        ...prev,
        [section]: false
      }));
      
      let message = '';
      switch (section) {
        case 'account':
          message = 'Account settings have been saved successfully.';
          break;
        case 'notifications':
          message = 'Notification preferences have been saved successfully.';
          break;
        case 'system':
          message = 'System configuration has been saved successfully.';
          break;
        case 'users':
          message = 'User management changes have been saved successfully.';
          break;
        case 'datasources':
          message = 'Data source settings have been saved successfully.';
          break;
        case 'devicegroups':
          message = 'Device group settings have been saved successfully.';
          break;
        case 'protocols':
          message = 'Protocol handler settings have been saved successfully.';
          break;
        case 'security':
          message = 'Security and access settings have been saved successfully.';
          break;
        case 'ai':
          message = 'AI and LLM integration settings have been saved successfully.';
          break;
        case 'billing':
          message = 'Tariff and billing settings have been saved successfully.';
          break;
        case 'sustainability':
          message = 'Sustainability settings have been saved successfully.';
          break;
        case 'webhooks':
          message = 'Webhooks and API settings have been saved successfully.';
          break;
      }
      
      toast({
        title: "Settings Updated",
        description: message
      });
    } catch (error) {
      console.error(`Error saving ${section} settings:`, error);
      toast({
        title: "Error Saving Settings",
        description: `There was a problem saving your ${section} settings. Please try again.`,
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [toast]);

  const cancelEdit = useCallback((section: SettingSection) => {
    setEditing(prev => ({
      ...prev,
      [section]: false
    }));
    
    toast({
      title: "Edit Cancelled",
      description: "Your changes were not saved."
    });
  }, [toast]);

  return {
    activeTab,
    setActiveTab,
    editing,
    isSubmitting,
    handleEditToggle,
    handleSave,
    cancelEdit
  };
};
